import React, { useId } from 'react';
import { Button } from './ui/button';
import Spinner from './ui/Spinner';
import MicrophonePicker from './MicrophonePicker';
import { COSELLER_MIC_COPY, WATCH_SESSION_COPY, WATCHER_COPY } from '../constants/watchSession';
import { useMicrophoneChoice } from '../hooks/useMicrophoneChoice';
import type { WatchableSession } from '../types/liveSession';

interface JoinChoiceStepProps {
  /** Someone else's live call, bot or desktop, picked from the list. */
  session: WatchableSession;
  /** A co-seller join is in flight: the options hide, Cancel is off, the joining state shows. */
  isJoining: boolean;
  /** Why the last co-seller join of this call failed; the step stays open to retry. */
  joinError: string | null;
  /** Join as Co-Seller with this microphone (null = the browser's default). */
  onJoinAsCoSeller: (deviceId: string | null) => void;
  /** Join as Observer: what Watch did. */
  onJoinAsObserver: () => void;
  onCancel: () => void;
}

interface ChoiceOptionProps {
  label: string;
  /** Muted helper text under the button, which also describes it. */
  explainer: string;
  variant: 'primary' | 'secondary';
  isDisabled?: boolean;
  onChoose: () => void;
  children?: React.ReactNode;
}

const ChoiceOption: React.FC<ChoiceOptionProps> = ({ label, explainer, variant, isDisabled = false, onChoose, children }) => {
  const explainerId = useId();
  return (
    <div>
      <Button
        variant={variant}
        size="sm"
        onClick={onChoose}
        disabled={isDisabled}
        aria-describedby={explainerId}
        className="text-xs"
      >
        {label}
      </Button>
      <p id={explainerId} className="text-[12px] text-ink-muted mt-1">
        {explainer}
      </p>
      {children}
    </div>
  );
};

const JoiningState: React.FC<{ ownerName: string | null }> = ({ ownerName }) => (
  <div className="flex items-center space-x-2 mt-3">
    <Spinner />
    <div>
      <p className="text-[13px] font-semibold text-ink">{COSELLER_MIC_COPY.joiningTitle(ownerName)}</p>
      <p className="text-[12px] text-ink-muted">{COSELLER_MIC_COPY.joiningDetail}</p>
    </div>
  </div>
);

/**
 * The step between a row's Join and the join (owner, 2026-10-07), the same
 * on macOS and WPF: Join "{session name}", then
 *  - a desktop call: Join as Co-Seller (primary; Rejoin as Co-Seller when the
 *    user already joined it with a mic) with its explainer and the microphone
 *    picker, then Join as Observer with its explainer;
 *  - a bot call: only Join as Observer, its explainer and the bot-call note
 *    (the bot hears everyone, so there is no co-seller mode and no mic);
 * then Cancel. While a co-seller join runs, the options hide behind
 * "Joining {owner}'s call…" / "Connecting your microphone"; a failure shows
 * here, in red, and the step stays open.
 */
const JoinChoiceStep: React.FC<JoinChoiceStepProps> = ({
  session,
  isJoining,
  joinError,
  onJoinAsCoSeller,
  onJoinAsObserver,
  onCancel,
}) => {
  const titleId = useId();
  const isDesktopCall = session?.mode === 'standalone';
  const microphoneChoice = useMicrophoneChoice(isDesktopCall);
  const isMicrophoneDenied = isDesktopCall && microphoneChoice.microphoneState === 'denied';
  const errorMessage = isMicrophoneDenied ? COSELLER_MIC_COPY.micAccessNeeded : joinError;
  const coSellerLabel = session?.joinedAs ? COSELLER_MIC_COPY.rejoinAsCoSeller : COSELLER_MIC_COPY.joinAsCoSeller;

  return (
    <div role="group" aria-labelledby={titleId} className="py-2">
      <h3 id={titleId} className="text-[15px] font-bold text-ink">
        {WATCH_SESSION_COPY.joinTitle(session?.sessionName || WATCH_SESSION_COPY.untitled)}
      </h3>

      {errorMessage && !isJoining && (
        <p role="alert" className="text-xs text-error mt-2">
          {errorMessage}
        </p>
      )}

      {isJoining ? (
        <JoiningState ownerName={session?.ownerName ?? null} />
      ) : (
        <div className="mt-3 space-y-3">
          {isDesktopCall && (
            <ChoiceOption
              label={coSellerLabel}
              explainer={COSELLER_MIC_COPY.coSellerExplainer}
              variant="primary"
              isDisabled={microphoneChoice.microphoneState !== 'ready'}
              onChoose={() => onJoinAsCoSeller(microphoneChoice.selectedDeviceId || null)}
            >
              <MicrophonePicker choice={microphoneChoice} />
            </ChoiceOption>
          )}
          <ChoiceOption
            label={WATCHER_COPY.joinAsObserver}
            explainer={WATCHER_COPY.observerExplainer}
            variant="secondary"
            onChoose={onJoinAsObserver}
          />
          {!isDesktopCall && <p className="text-[12px] text-ink-muted">{COSELLER_MIC_COPY.botCallNote}</p>}
        </div>
      )}

      <div className="flex justify-end mt-4">
        <Button variant="secondary" size="sm" onClick={onCancel} disabled={isJoining} className="text-xs">
          {COSELLER_MIC_COPY.cancel}
        </Button>
      </div>
    </div>
  );
};

export default JoinChoiceStep;
